import { useState, useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { writeAuditLog } from '@/lib/audit';
import { SECTION_WEIGHTS } from '@/lib/assemblySections';
import { openPauseForUnit, pauseReasonLabel, pauseScopeLabel } from '@/lib/pauses';
import { useOrderPauses } from '@/lib/api/pauses';
import { OpenPauses, PauseButton } from '@/components/BuildPauses';
import { CHECKLIST_EVENTS_KEY, useChecklistEvents } from '@/lib/api/team';
import { currentTicks, techLabel } from '@/lib/teamwork';
import { toast } from 'sonner';

type AoStatus = 'pending' | 'building' | 'software' | 'testing' | 'awaiting_review' | 'completed';

export interface ChecklistItemDef {
  key: string;
  label: string;
}

export interface ChecklistSectionDef {
  key: string;
  label: string;
  /** Status to advance to when this section is fully completed (only moves forward). */
  triggerStatus: AoStatus | null;
  items: ChecklistItemDef[];
}

export const CHECKLIST_SECTIONS: ChecklistSectionDef[] = [
  {
    key: 'assembly',
    label: 'Assembly',
    triggerStatus: 'software',
    items: [
      { key: 'components', label: 'Components' },
      { key: 'cable_management', label: 'Cable Management' },
    ],
  },
  {
    key: 'software',
    label: 'Software',
    triggerStatus: 'testing',
    items: [
      { key: 'windows_installation', label: 'Windows Installation' },
      { key: 'drive_initialization', label: 'Drive/s Initialization + Volume Creation' },
      { key: 'windows_updates', label: 'Windows Updates' },
      { key: 'model_specific', label: 'Model-Specific changes (Model name, app cleanup etc)' },
      { key: 'driver_updates', label: 'Driver Updates (Motherboard + GPU)' },
      { key: 'bios_update', label: 'BIOS Update' },
      { key: 'ram_profile', label: 'RAM Profile (XMP/EXPO)' },
    ],
  },
  {
    key: 'benchmarking',
    label: 'Benchmarking',
    triggerStatus: null,
    items: [
      { key: 'furmark_stress', label: 'FURMark Stress Test' },
      { key: 'passmark', label: 'PassMark Benchmark' },
    ],
  },
  {
    key: 'finishing',
    label: 'Finishing',
    triggerStatus: null,
    items: [
      { key: 'windows_activation', label: 'Windows Activation' },
      { key: 'uninstall_tools', label: 'Uninstall tools (FURMark/PassMark)' },
      { key: 'delete_accounts', label: "Delete existing user accounts (for Display PC's)" },
      { key: 'oobe', label: 'OOBE' },
    ],
  },
  {
    key: 'final_touches',
    label: 'Final Touches',
    triggerStatus: 'awaiting_review',
    items: [
      { key: 'assembly_certificate', label: 'Assembly Certificate' },
      { key: 'branding_stickers', label: 'Branding Stickers (Model Name, AOI Logo)' },
      { key: 'warranty_seals', label: 'Warranty Seals' },
      { key: 'windows_license', label: 'Windows License Sticker' },
      { key: 'sku_labels', label: 'SKU & S/N Labels' },
      { key: 'final_wipe', label: 'Final Wipe down' },
      { key: 'pack_seal', label: 'Pack & Seal' },
      { key: 'sign_off', label: 'Sign-Off Sheet' },
    ],
  },
];

// SECTION_WEIGHTS moved to lib/assemblySections.ts so lib/stageTimeline.ts can
// use it without importing this component (and, through it, React and the
// Supabase client). Re-exported so existing importers keep working.
export { SECTION_WEIGHTS };

export const SECTION_COLORS: Record<string, string> = {
  assembly: 'bg-blue-500',
  software: 'bg-indigo-500',
  benchmarking: 'bg-amber-500',
  finishing: 'bg-emerald-500',
  final_touches: 'bg-green-500',
};

const STATUS_ORDER: AoStatus[] = ['pending', 'building', 'software', 'testing', 'awaiting_review', 'completed'];

// ── Multi-unit helpers ──────────────────────────────────────────────

type UnitProgress = Record<string, boolean>;
type MultiUnitProgress = Record<string, UnitProgress>; // { "1": {...}, "2": {...} }

/**
 * Normalise checklist_progress from DB into per-unit format.
 * Old flat format (single unit) is migrated to { "1": { ...flat } }.
 */
export function normaliseProgress(raw: any, unitCount: number): MultiUnitProgress {
  if (!raw || typeof raw !== 'object') {
    const empty: MultiUnitProgress = {};
    for (let i = 1; i <= unitCount; i++) empty[String(i)] = {};
    return empty;
  }
  // Already multi-unit format: has string-number keys with object values
  if (raw['1'] && typeof raw['1'] === 'object' && !Array.isArray(raw['1'])) {
    const result: MultiUnitProgress = {};
    for (let i = 1; i <= unitCount; i++) result[String(i)] = raw[String(i)] ?? {};
    return result;
  }
  // Old flat format — migrate to unit 1
  const result: MultiUnitProgress = { '1': raw as UnitProgress };
  for (let i = 2; i <= unitCount; i++) result[String(i)] = {};
  return result;
}

/** Calculate weighted progress % for a single unit's progress record. */
export function calcWeightedPct(unitProg: UnitProgress): number {
  return CHECKLIST_SECTIONS.reduce((total, section) => {
    const weight = SECTION_WEIGHTS[section.key] || 0;
    const done = section.items.filter(i => unitProg[i.key]).length;
    const pct = section.items.length > 0 ? done / section.items.length : 0;
    return total + weight * pct;
  }, 0);
}

/** Calculate aggregate weighted progress across all units. */
export function calcAggregateProgress(allUnits: MultiUnitProgress): number {
  const unitKeys = Object.keys(allUnits);
  if (!unitKeys.length) return 0;
  const total = unitKeys.reduce((sum, k) => sum + calcWeightedPct(allUnits[k]), 0);
  return Math.round(total / unitKeys.length);
}

/** Determine the least-advanced status across all units based on checklist completion. */
export function getLeastAdvancedStatus(allUnits: MultiUnitProgress, currentStatus: AoStatus): AoStatus {
  // For each unit, find the furthest completed section trigger
  const unitStatuses = Object.values(allUnits).map(unitProg => {
    let furthest: AoStatus = 'building'; // default once started
    for (const section of CHECKLIST_SECTIONS) {
      if (!section.triggerStatus) continue;
      const complete = section.items.every(i => unitProg[i.key]);
      if (complete) {
        const triggerIdx = STATUS_ORDER.indexOf(section.triggerStatus);
        const furthestIdx = STATUS_ORDER.indexOf(furthest);
        if (triggerIdx > furthestIdx) furthest = section.triggerStatus;
      }
    }
    return furthest;
  });
  // Return the minimum (least advanced)
  const minIdx = Math.min(...unitStatuses.map(s => STATUS_ORDER.indexOf(s)));
  const computed = STATUS_ORDER[minIdx];
  // Only advance forward from current, never go back via checklist
  const currentIdx = STATUS_ORDER.indexOf(currentStatus);
  return STATUS_ORDER.indexOf(computed) > currentIdx ? computed : currentStatus;
}

/** A section completed this soon after its first tick was ticked after the
 *  work, not as it happened — no multi-step section takes under a minute. */
export const BURST_WINDOW_MS = 60_000;

/**
 * True when this toggle completes `section` for a unit in a burst: in one
 * click, or within BURST_WINDOW_MS of the section's first tick. Such a stage
 * shows as ~0m on the order timeline.
 *
 * Not flagged when another unit has already completed the section — on a
 * multi-unit order the tech works the units in parallel and catching up the
 * remaining units at once is expected.
 */
export function isBurstCompletion(
  section: ChecklistSectionDef,
  prevUnitProg: UnitProgress,
  newUnitProg: UnitProgress,
  sectionStartedAt: string | null | undefined,
  otherUnits: UnitProgress[],
  now: Date,
): boolean {
  if (section.items.length < 2) return false;
  const wasComplete = section.items.every(i => prevUnitProg[i.key]);
  const isComplete = section.items.every(i => newUnitProg[i.key]);
  if (wasComplete || !isComplete) return false;
  if (otherUnits.some(up => section.items.every(i => up[i.key]))) return false;
  if (!sectionStartedAt) {
    // No recorded first tick: a burst only if nothing was ticked before
    // (otherwise it's a legacy order with no per-unit timestamps).
    return !section.items.some(i => prevUnitProg[i.key]);
  }
  return now.getTime() - new Date(sectionStartedAt).getTime() < BURST_WINDOW_MS;
}

// ── Saving ──────────────────────────────────────────────────────────

interface ChecklistState {
  progress: any;
  timestamps: Record<string, any>;
  version: number;
  status: AoStatus;
}

interface SavedChecklist {
  progress: MultiUnitProgress;
  timestamps: Record<string, any>;
  version: number;
  newStatus?: AoStatus;
}

async function readChecklist(orderId: string): Promise<ChecklistState> {
  const { data, error } = await supabase
    .from('assembly_orders')
    .select('checklist_progress, stage_timestamps, checklist_version, status')
    .eq('id', orderId)
    .single();
  if (error) throw error;
  return {
    progress: data.checklist_progress,
    timestamps: data.stage_timestamps || {},
    version: data.checklist_version ?? 0,
    status: (data.status as AoStatus) ?? 'pending',
  };
}

/**
 * Stage timestamps after a checklist change: a section starts at its first
 * tick and completes when every item is ticked, per unit and (across every
 * unit) for the order. A section that is no longer complete loses its
 * completion time; start times are kept.
 */
export function nextStageTimestamps(
  previous: Record<string, any> | null | undefined,
  newAllProgress: MultiUnitProgress,
  now: string,
): Record<string, any> {
  const timestamps: Record<string, any> = { ...(previous || {}) };
  const prevUnits = timestamps.units && typeof timestamps.units === 'object' ? timestamps.units : {};
  const units: Record<string, Record<string, string | null>> = {};
  for (const [k, v] of Object.entries(prevUnits)) units[k] = { ...(v as Record<string, string | null>) };

  for (const section of CHECKLIST_SECTIONS) {
    const startedKey = `${section.key}_started`;
    const completedKey = `${section.key}_completed`;

    for (const unitKey of Object.keys(newAllProgress)) {
      if (!units[unitKey]) units[unitKey] = {};
      const unitProg = newAllProgress[unitKey];
      const unitComplete = section.items.every(i => unitProg[i.key]);
      const unitAnyTicked = section.items.some(i => unitProg[i.key]);

      if (unitComplete) {
        if (!units[unitKey][startedKey]) units[unitKey][startedKey] = now;
        if (!units[unitKey][completedKey]) units[unitKey][completedKey] = now;
      } else {
        if (unitAnyTicked && !units[unitKey][startedKey]) units[unitKey][startedKey] = now;
        units[unitKey][completedKey] = null;
      }
    }

    const allUnitsComplete = Object.values(newAllProgress).every(up =>
      section.items.every(i => up[i.key])
    );
    if (allUnitsComplete) {
      if (!timestamps[startedKey]) timestamps[startedKey] = now;
      if (!timestamps[completedKey]) timestamps[completedKey] = now;
    } else {
      const anyTicked = Object.values(newAllProgress).some(up =>
        section.items.some(i => up[i.key])
      );
      if (anyTicked && !timestamps[startedKey]) timestamps[startedKey] = now;
      timestamps[completedKey] = null;
    }
  }
  timestamps.units = units;
  return timestamps;
}

// ── Component ───────────────────────────────────────────────────────

interface AssemblyChecklistProps {
  order: any;
  readOnly?: boolean;
  onStatusChange?: (newStatus: AoStatus, newProgress: MultiUnitProgress) => void;
}

export function AssemblyChecklist({ order, readOnly = false, onStatusChange }: AssemblyChecklistProps) {
  const queryClient = useQueryClient();
  const unitCount = Math.max(order?.quantity || 1, 1);
  const [allProgress, setAllProgress] = useState<MultiUnitProgress>(
    normaliseProgress(order?.checklist_progress, unitCount)
  );
  const [activeUnit, setActiveUnit] = useState('1');
  // Keep stage_timestamps in local state so rapid checkbox clicks don't overwrite
  // previously saved values with stale prop data.
  const [localTimestamps, setLocalTimestamps] = useState<Record<string, any>>(
    order?.stage_timestamps || {}
  );
  // The checklist_version this screen's state was read at; null = unknown, so
  // the first save reads the row first.
  const [version, setVersion] = useState<number | null>(
    typeof order?.checklist_version === 'number' ? order.checklist_version : null
  );
  const { data: events = [] } = useChecklistEvents(order?.id);

  const progress = allProgress[activeUnit] ?? {};
  // The checklist is locked until Start Building is pressed, so `started_at`
  // is always that deliberate click and never the first checklist tick.
  const notStarted = ((order?.status as AoStatus) ?? 'pending') === 'pending';
  // A paused unit can't be ticked: work on it has stopped, and a tick would
  // record progress inside time that comes off the assembly duration.
  const { data: pauses = [] } = useOrderPauses(order?.id);
  const activePause = openPauseForUnit(pauses, Number(activeUnit));
  const locked = readOnly || notStarted || !!activePause;

  const warnIfBurst = useCallback(
    (section: ChecklistSectionDef, newUnitProg: UnitProgress) => {
      const units = (localTimestamps as any).units ?? {};
      const otherUnits = Object.entries(allProgress)
        .filter(([k]) => k !== activeUnit)
        .map(([, up]) => up);
      const burst = isBurstCompletion(
        section,
        progress,
        newUnitProg,
        units[activeUnit]?.[`${section.key}_started`],
        otherUnits,
        new Date(),
      );
      if (burst) {
        toast.warning(
          `${section.label} was ticked all at once, so the order timeline will show it as taking no time. Tick each step as you finish it.`,
          { duration: 8000 },
        );
      }
    },
    [localTimestamps, allProgress, activeUnit, progress]
  );

  // Teammates work the same order, so the checklist on screen can fall behind
  // the database. A light poll keeps it current; a save that still races a
  // teammate's is caught by the version check in the mutation below.
  const { data: live } = useQuery({
    queryKey: ['checklist-live', order?.id],
    queryFn: () => readChecklist(order.id),
    enabled: !!order?.id && !readOnly,
    refetchInterval: 20_000,
  });

  const mutation = useMutation({
    mutationFn: async ({
      unit,
      keys,
      value,
    }: {
      unit: string;
      keys: string[];
      value: boolean;
      auditMeta?: { changedItems: { key: string; label: string; newValue: boolean }[]; unit: string; section?: string };
    }): Promise<SavedChecklist> => {
      // Start from what this screen shows; if a teammate has saved since,
      // re-read and apply the same change on top of theirs.
      let base: ChecklistState = version != null
        ? { progress: allProgress, timestamps: localTimestamps, version, status: (order.status as AoStatus) ?? 'pending' }
        : await readChecklist(order.id);

      for (let attempt = 0; attempt < 4; attempt++) {
        const progress = normaliseProgress(base.progress, unitCount);
        const newAll: MultiUnitProgress = { ...progress, [unit]: { ...(progress[unit] ?? {}) } };
        for (const k of keys) newAll[unit][k] = value;

        const computedStatus = computeStatusFromChecklist(newAll);
        const newStatus = computedStatus !== base.status ? computedStatus : undefined;
        const timestamps = nextStageTimestamps(base.timestamps, newAll, new Date().toISOString());

        const updates: Record<string, any> = { checklist_progress: newAll, stage_timestamps: timestamps };
        if (newStatus) {
          updates.status = newStatus;
          if (newStatus === 'awaiting_review') updates.completed_at = new Date().toISOString();
        }

        const { data, error } = await supabase
          .from('assembly_orders')
          .update(updates)
          .eq('id', order.id)
          .eq('checklist_version', base.version)
          .select('checklist_version');
        if (error) throw error;
        if (data?.length) {
          return { progress: newAll, timestamps, version: data[0].checklist_version, newStatus };
        }

        // Nothing written: either someone saved first, or this account may
        // not work the order. A re-read tells the two apart.
        const fresh = await readChecklist(order.id);
        if (fresh.version === base.version) {
          throw new Error('you may not be on this order\'s team');
        }
        base = fresh;
      }
      throw new Error('the checklist kept changing - try again');
    },
    onSuccess: (saved, { auditMeta }) => {
      const { newStatus } = saved;
      setAllProgress(saved.progress);
      setLocalTimestamps(saved.timestamps);
      setVersion(saved.version);
      queryClient.invalidateQueries({ queryKey: ['active-assembly-orders'] });
      queryClient.invalidateQueries({ queryKey: ['technician-orders'] });
      queryClient.invalidateQueries({ queryKey: ['assembly-order-status-counts'] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      queryClient.invalidateQueries({ queryKey: [...CHECKLIST_EVENTS_KEY, order.id] });
      if (newStatus) {
        toast.success(`Status advanced to: ${newStatus.replace(/_/g, ' ')}`);
        onStatusChange?.(newStatus, saved.progress);
      }
      // Audit log for checklist changes
      if (auditMeta) {
        const label = order.build_sku || order.id;
        for (const item of auditMeta.changedItems) {
          writeAuditLog({
            action: 'assembly_order_checklist_updated',
            entity_type: 'assembly_order',
            entity_id: order.id,
            entity_label: label,
            old_value: { [item.key]: !item.newValue },
            new_value: { [item.key]: item.newValue },
            notes: `Unit ${auditMeta.unit}: ${item.label} ${item.newValue ? 'checked' : 'unchecked'}${auditMeta.section ? ` (section: ${auditMeta.section})` : ''}`,
          });
        }
      }
    },
    onError: (err: any) => {
      toast.error(`Failed to save: ${err.message}`);
    },
  });

  // Adopt a newer checklist when a teammate has saved one - from the poll, or
  // from the order passed in once its page refetches. Never mid-save: the
  // save itself ends on the latest version.
  const newest = [live, typeof order?.checklist_version === 'number'
    ? { progress: order.checklist_progress, timestamps: order.stage_timestamps, version: order.checklist_version }
    : null]
    .filter((c): c is Pick<ChecklistState, 'progress' | 'timestamps' | 'version'> => !!c)
    .sort((a, b) => b.version - a.version)[0];
  useEffect(() => {
    if (!newest || mutation.isPending) return;
    if (version != null && newest.version <= version) return;
    setAllProgress(normaliseProgress(newest.progress, unitCount));
    setLocalTimestamps(newest.timestamps || {});
    setVersion(newest.version);
    queryClient.invalidateQueries({ queryKey: [...CHECKLIST_EVENTS_KEY, order.id] });
  }, [newest?.version, mutation.isPending]); // eslint-disable-line react-hooks/exhaustive-deps

  // Who ticked each step still ticked, for the initials beside it.
  const ticks = currentTicks(events, allProgress);

  /**
   * Compute the correct status based on overall checklist state across all units.
   * Walks through sections in order — the status matches the highest completed
   * trigger where ALL units have that section done. Rolls back if sections are
   * unticked, but never below `building`: only Start Building moves an order
   * out of `pending`, and nothing in the checklist moves it back.
   */
  const computeStatusFromChecklist = useCallback(
    (newAll: MultiUnitProgress): AoStatus => {
      // Walk sections in order; status = last completed trigger across all units
      // Never auto-advance to awaiting_review — that requires explicit sign-off
      let status: AoStatus = 'building';
      for (const section of CHECKLIST_SECTIONS) {
        if (!section.triggerStatus) continue;
        const allUnitsComplete = Object.values(newAll).every(up =>
          section.items.every(i => up[i.key])
        );
        if (allUnitsComplete) {
          if (section.triggerStatus === 'awaiting_review') continue;
          status = section.triggerStatus;
        } else {
          break; // Stop at first incomplete trigger section
        }
      }
      return status;
    },
    []
  );

  const handleToggle = useCallback(
    (itemKey: string, _section: ChecklistSectionDef) => {
      if (locked || mutation.isPending) return;

      const prevAll = allProgress;
      const newValue = !progress[itemKey];
      const unitProg = { ...progress, [itemKey]: newValue };
      const newAll = { ...allProgress, [activeUnit]: unitProg };
      setAllProgress(newAll);
      warnIfBurst(_section, unitProg);

      const itemDef = _section.items.find(i => i.key === itemKey);
      mutation.mutate(
        {
          unit: activeUnit,
          keys: [itemKey],
          value: newValue,
          auditMeta: {
            changedItems: [{ key: itemKey, label: itemDef?.label ?? itemKey, newValue }],
            unit: activeUnit,
            section: _section.label,
          },
        },
        { onError: () => setAllProgress(prevAll) }
      );
    },
    [allProgress, activeUnit, progress, locked, mutation, warnIfBurst]
  );

  const handleToggleSection = useCallback(
    (section: ChecklistSectionDef) => {
      if (locked || mutation.isPending) return;

      const prevAll = allProgress;
      const allChecked = section.items.every(i => progress[i.key]);
      const newValue = !allChecked;
      const unitProg = { ...progress };
      section.items.forEach(i => { unitProg[i.key] = newValue; });
      const newAll = { ...allProgress, [activeUnit]: unitProg };
      setAllProgress(newAll);
      warnIfBurst(section, unitProg);

      const changedItems = section.items
        .filter(i => !!progress[i.key] !== newValue)
        .map(i => ({ key: i.key, label: i.label, newValue }));

      mutation.mutate(
        {
          unit: activeUnit,
          keys: section.items.map(i => i.key),
          value: newValue,
          auditMeta: {
            changedItems,
            unit: activeUnit,
            section: section.label,
          },
        },
        { onError: () => setAllProgress(prevAll) }
      );
    },
    [allProgress, activeUnit, progress, locked, mutation, warnIfBurst]
  );

  const allItems = CHECKLIST_SECTIONS.flatMap((s) => s.items);
  const doneCount = allItems.filter((i) => progress[i.key]).length;

  // Weighted progress for the active unit
  const pct = Math.round(calcWeightedPct(progress));

  // Aggregate progress across all units
  const aggPct = calcAggregateProgress(allProgress);

  return (
    <div className="space-y-3">
      {notStarted && !readOnly && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          This order hasn't been started. Press <span className="font-semibold">Start Building</span> on
          the order before you begin. That click is recorded as the build's start time.
        </div>
      )}

      {/* Pauses: running ones, and the button to start one */}
      {!notStarted && (
        <div className="space-y-2">
          <OpenPauses order={order} pauses={pauses} canResume={!readOnly} />
          {!readOnly && (
            <div className="flex justify-end">
              <PauseButton order={order} pauses={pauses} defaultUnit={unitCount > 1 ? Number(activeUnit) : null} />
            </div>
          )}
        </div>
      )}

      {/* Unit tabs (only if multi-unit) */}
      {unitCount > 1 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Unit:</span>
            <div className="flex gap-1">
              {Array.from({ length: unitCount }, (_, i) => {
                const unitKey = String(i + 1);
                const unitProg = allProgress[unitKey] ?? {};
                const unitDone = allItems.every(item => unitProg[item.key]);
                const unitPctVal = Math.round(calcWeightedPct(unitProg));
                return (
                  <button
                    key={unitKey}
                    onClick={() => setActiveUnit(unitKey)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                      activeUnit === unitKey
                        ? 'bg-primary text-primary-foreground border-primary'
                        : unitDone
                        ? 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100'
                        : 'bg-background hover:bg-muted border-border'
                    }`}
                  >
                    Unit {unitKey}
                    {openPauseForUnit(pauses, i + 1) && (
                      <span className="ml-1 text-amber-600" title="Paused">⏸</span>
                    )}
                    {unitPctVal > 0 && unitPctVal < 100 && (
                      <span className="ml-1 opacity-70">{unitPctVal}%</span>
                    )}
                    {unitDone && <span className="ml-1">&#10003;</span>}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Aggregate progress bar */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground whitespace-nowrap">Overall:</span>
            <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${aggPct}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">{aggPct}%</span>
          </div>
        </div>
      )}

      {activePause && !readOnly && (
        <p className="text-xs text-amber-700 dark:text-amber-500">
          {unitCount > 1 ? `Unit ${activeUnit}` : 'This build'} is paused
          {activePause.unit_number == null && unitCount > 1 ? ` (${pauseScopeLabel(activePause).toLowerCase()})` : ''}
          {' '}for {pauseReasonLabel(activePause.reason).toLowerCase()}. Resume it to carry on ticking.
        </p>
      )}

      {/* Per-unit weighted progress bar */}
      <div className="flex items-center gap-3">
        {unitCount > 1 && <span className="text-xs text-muted-foreground whitespace-nowrap">Unit {activeUnit}:</span>}
        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden flex">
          {CHECKLIST_SECTIONS.map((section) => {
            const weight = SECTION_WEIGHTS[section.key] || 0;
            const sectionDone = section.items.filter((i) => progress[i.key]).length;
            const sectionPct = section.items.length > 0 ? sectionDone / section.items.length : 0;
            return (
              <div key={section.key} className="flex" style={{ width: `${weight}%` }}>
                {sectionPct > 0 && (
                  <div
                    className={`h-full ${SECTION_COLORS[section.key]} transition-all duration-300`}
                    style={{ width: `${(sectionPct * 100)}%` }}
                  />
                )}
                {sectionPct < 1 && (
                  <div
                    className="h-full"
                    style={{ width: `${((1 - sectionPct) * 100)}%` }}
                  />
                )}
              </div>
            );
          })}
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {doneCount}/{allItems.length} ({pct}%)
        </span>
      </div>

      {/* Sections */}
      {CHECKLIST_SECTIONS.map((section) => {
        const completedCount = section.items.filter((i) => progress[i.key]).length;
        const isComplete = completedCount === section.items.length;

        // For status trigger badge, check if ALL units have this section complete
        const allUnitsComplete = unitCount > 1
          ? Object.values(allProgress).every(up => section.items.every(i => up[i.key]))
          : isComplete;

        return (
          <div
            key={section.key}
            className={`border rounded-lg overflow-hidden transition-colors ${
              isComplete ? 'border-green-300' : 'border-border'
            }`}
          >
            <div
              className={`flex items-center justify-between px-3 py-2 ${
                isComplete ? 'bg-green-50' : 'bg-muted/40'
              } ${!locked ? 'cursor-pointer hover:opacity-80' : ''}`}
              onClick={() => handleToggleSection(section)}
            >
              <div className="flex items-center gap-2">
                {!readOnly && (
                  <input
                    type="checkbox"
                    checked={isComplete}
                    readOnly
                    className="h-4 w-4 rounded border-gray-300 accent-primary pointer-events-none"
                  />
                )}
                <span className={`text-sm font-bold ${isComplete ? 'text-green-700' : ''}`}>
                  {section.label}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {section.triggerStatus && allUnitsComplete && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                    → {section.triggerStatus.replace(/_/g, ' ')}
                  </span>
                )}
                <span
                  className={`text-xs font-medium ${
                    isComplete ? 'text-green-600' : 'text-muted-foreground'
                  }`}
                >
                  {completedCount}/{section.items.length}
                </span>
              </div>
            </div>

            <div className="divide-y">
              {section.items.map((item) => {
                const checked = !!progress[item.key];
                return (
                  <label
                    key={item.key}
                    className={`flex items-center gap-3 px-3 py-2 select-none transition-colors ${
                      locked ? 'cursor-default' : 'cursor-pointer hover:bg-muted/30'
                    } ${notStarted && !readOnly ? 'opacity-50' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggle(item.key, section)}
                      disabled={locked || mutation.isPending}
                      className="h-4 w-4 rounded border-gray-300 accent-primary"
                    />
                    <span className={`text-sm ${checked ? 'line-through text-muted-foreground' : ''}`}>
                      {item.label}
                    </span>
                    {checked && (() => {
                      const tick = ticks.get(`${activeUnit}:${item.key}`);
                      if (!tick?.technician) return null;
                      return (
                        <span
                          className="ml-auto text-[10px] font-medium text-muted-foreground border rounded px-1"
                          title={`Ticked by ${techLabel(tick.technician)}, ${new Date(tick.created_at).toLocaleString()}`}
                        >
                          {tick.technician.initials || techLabel(tick.technician)}
                        </span>
                      );
                    })()}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
